import dayjs from "dayjs";
import React, { useMemo, useRef, useEffect, useState } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Tooltip,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import { ClipLoader } from "react-spinners";
import "../sales-mockup.css";
import { BRAND_COLORS, getCssToken } from "../../../styles/brandColors";

// Register Chart.js components
ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Tooltip,
);

// ChartErrorBoundary removed - react-chartjs-2 handles errors gracefully

interface ChartsModernizedProps {
  salesgraphs?: {
    donutOpen: number;
    donutClosed: number;
    monthOneOrdersCount: number;
    monthTwoOrdersCount: number;
    monthThreeOrdersCount: number;
    monthOneExchangesCount: number;
    monthTwoExchangesCount: number;
    monthThreeExchangesCount: number;
  };
}

const ChartsModernized: React.FC<ChartsModernizedProps> = ({
  salesgraphs,
}) => {
  // react-chartjs-2 handles cleanup automatically, refs are optional
  const chartRefDonut = useRef<ChartJS<"doughnut">>(null);
  const chartRefBar = useRef<ChartJS<"bar">>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  const getMonths = () => {
    const monthThree = dayjs().startOf("month").format("MMMM");
    const monthTwo = dayjs()
      .subtract(1, "months")
      .startOf("month")
      .format("MMMM");
    const monthOne = dayjs()
      .subtract(2, "months")
      .startOf("month")
      .format("MMMM");

    return [monthOne, monthTwo, monthThree];
  };

  const months = useMemo(() => getMonths(), []);

  const toNumber = (v: number | undefined): number => {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const donutData = useMemo(() => {
    if (!salesgraphs) return null;
    return {
      labels: ["Open", "Closed"],
      datasets: [
        {
          data: [
            toNumber(salesgraphs.donutOpen),
            toNumber(salesgraphs.donutClosed),
          ],
          backgroundColor: [BRAND_COLORS.ajwPurple, BRAND_COLORS.ajwRed],
          hoverBackgroundColor: [
            getCssToken("--ajw-purple-hover"),
            getCssToken("--ajw-red-hover"),
          ],
          borderWidth: 0,
        },
      ],
    };
  }, [salesgraphs]);

  const barData = useMemo(() => {
    if (!salesgraphs) return null;
    const barOrders = [
      toNumber(salesgraphs.monthOneOrdersCount),
      toNumber(salesgraphs.monthTwoOrdersCount),
      toNumber(salesgraphs.monthThreeOrdersCount),
    ];
    const barExchanges = [
      toNumber(salesgraphs.monthOneExchangesCount),
      toNumber(salesgraphs.monthTwoExchangesCount),
      toNumber(salesgraphs.monthThreeExchangesCount),
    ];

    return {
      labels: months,
      datasets: [
        {
          label: "Sales",
          backgroundColor: BRAND_COLORS.ajwPurple,
          hoverBackgroundColor: getCssToken("--ajw-purple-hover"),
          data: barOrders,
        },
        {
          label: "Exchanges",
          backgroundColor: BRAND_COLORS.ajwBlue,
          hoverBackgroundColor: getCssToken("--ajw-blue-darker"),
          data: barExchanges,
        },
      ],
    };
  }, [salesgraphs, months]);

  const isValidDonutData =
    donutData &&
    donutData.datasets &&
    donutData.datasets[0] &&
    Array.isArray(donutData.datasets[0].data) &&
    donutData.datasets[0].data.length === 2 &&
    donutData.datasets[0].data.every(
      (d) => typeof d === "number" && !isNaN(d),
    );

  const isValidBarData =
    barData &&
    barData.datasets &&
    barData.datasets[0] &&
    barData.datasets[1] &&
    barData.datasets[0].data &&
    barData.datasets[1].data &&
    barData.datasets[0].data.every((n) => Number.isFinite(n)) &&
    barData.datasets[1].data.every((n) => Number.isFinite(n));

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1.5,
    layout: {
      padding: { top: 4, bottom: 4, left: 4, right: 4 },
    },
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          padding: 10,
          boxWidth: 11,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        backgroundColor: getCssToken("--ajw-purple-95"),
        padding: 10,
        titleFont: {
          size: 12,
        },
        bodyFont: {
          size: 11,
        },
      },
    },
  };

  const stackedChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: isMobile ? 1.2 : 2,
    layout: {
      padding: { top: 4, bottom: 4, left: 4, right: 4 },
    },
    plugins: {
      tooltip: {
        mode: "index" as const,
        intersect: false,
        backgroundColor: getCssToken("--ajw-purple-95"),
        padding: 9,
      },
      legend: {
        position: "top" as const,
        labels: {
          padding: 10,
          boxWidth: 11,
          font: {
            size: isMobile ? 10 : 11,
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          color: getCssToken("--ajw-grey-10"),
        },
        ticks: {
          font: {
            size: isMobile ? 9 : 11,
          },
        },
      },
      y: {
        stacked: true,
        grid: {
          color: getCssToken("--ajw-grey-10"),
        },
        ticks: {
          font: {
            size: isMobile ? 9 : 11,
          },
        },
      },
    },
  }), [isMobile]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="charts-container" data-testid="sales-overview-charts">
      {/* Unified Chart Card */}
      <div className="chart-card-unified">
        <div className="chart-card-header">
          <h3 className="chart-card-main-title">Sales Overview</h3>
        </div>
        
        <div className="chart-card-content">
          {/* Doughnut Chart Section */}
          <div className="chart-section">
            <h4 className="chart-title">
              General Summary of Last Three Months
            </h4>
            <div className="chart-content" style={{ position: "relative", width: "100%", maxWidth: "100%", overflow: "hidden" }}>
              {!salesgraphs ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "153px",
                    gap: "0.75rem",
                  }}
                >
                  <ClipLoader color="var(--ajw-blue)" size={36} />
                  <div
                    style={{
                      color: "var(--ajw-grey)",
                      fontSize: "0.84375rem",
                    }}
                  >
                    Loading chart data...
                  </div>
                </div>
              ) : isValidDonutData &&
                donutData &&
                donutData.datasets &&
                donutData.datasets[0] &&
                (donutData.datasets[0].data[0] !== 0 ||
                  donutData.datasets[0].data[1] !== 0) ? (
                <div id="doughnutChart" className="chart-content--donut-inner">
                  <Doughnut
                    ref={chartRefDonut}
                    key={`donut-${donutData.datasets[0].data.join("-")}`}
                    data={donutData}
                    options={doughnutChartOptions}
                  />
                </div>
              ) : (
                <div style={{ color: "var(--ajw-grey)", fontSize: "0.875rem" }}>
                  {salesgraphs
                    ? "No General Summary data from the last three months"
                    : "Loading chart data..."}
                </div>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="chart-divider"></div>

          {/* Bar Chart Section */}
          <div className="chart-section">
            <h4 className="chart-title">Last Three Months</h4>
            <div className="chart-content" style={{ position: "relative", width: "100%", maxWidth: "100%", overflow: "hidden" }}>
              {!salesgraphs ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "153px",
                    gap: "0.75rem",
                  }}
                >
                  <ClipLoader color="var(--ajw-blue)" size={36} />
                  <div
                    style={{
                      color: "var(--ajw-grey)",
                      fontSize: "0.84375rem",
                    }}
                  >
                    Loading chart data...
                  </div>
                </div>
              ) : isValidBarData && barData && barData.datasets ? (
                <div id="barChart" className="chart-content--bar-inner">
                  <Bar
                    ref={chartRefBar}
                    key={`bar-${barData.datasets[0].data.join("-")}-${barData.datasets[1].data.join("-")}`}
                    data={barData}
                    options={stackedChartOptions}
                  />
                </div>
              ) : (
                <div style={{ color: "var(--ajw-grey)", fontSize: "0.875rem" }}>
                  {salesgraphs
                    ? "Chart data could not be found"
                    : "Loading chart data..."}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartsModernized;
